import React, { useState } from 'react';
import { Send, X, Check, Users, Search } from 'lucide-react';

const TeamInvite = ({ teamId, teamName, onClose }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [inviteSent, setInviteSent] = useState(false);
  
  // Sample user data - in a real app, this would come from an API
  const [searchResults, setSearchResults] = useState([
    { id: 101, name: 'Emily Parker', avatar: '/api/placeholder/40/40', weeklyAvg: '12,456 steps' },
    { id: 102, name: 'Alex Johnson', avatar: '/api/placeholder/40/40', weeklyAvg: '9,872 steps' },
    { id: 103, name: 'Taylor Swift', avatar: '/api/placeholder/40/40', weeklyAvg: '15,233 steps' },
    { id: 104, name: 'James Wilson', avatar: '/api/placeholder/40/40', weeklyAvg: '8,654 steps' },
    { id: 105, name: 'Olivia Miller', avatar: '/api/placeholder/40/40', weeklyAvg: '11,432 steps' }
  ]);
  
  const filteredResults = searchQuery 
    ? searchResults.filter(user => 
        user.name.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : searchResults;
  
  const toggleUserSelection = (userId) => {
    if (selectedUsers.includes(userId)) {
      setSelectedUsers(selectedUsers.filter(id => id !== userId));
    } else {
      setSelectedUsers([...selectedUsers, userId]);
    }
  };
  
  const sendInvites = () => {
    // In a real app, you would make an API call here
    console.log(`Sending invites to ${selectedUsers.length} users for team ${teamId}`);
    setInviteSent(true);
    
    // Reset after 3 seconds
    setTimeout(() => {
      setSelectedUsers([]);
      setInviteSent(false);
      onClose();
    }, 3000);
  };
  
  return (
    <div className="bg-white rounded-xl shadow-sm">
      <div className="p-4 border-b flex justify-between items-center">
        <div className="flex items-center">
          <Users size={20} className="text-primary mr-2" />
          <h2 className="text-lg font-semibold">Invite to {teamName}</h2>
        </div>
        <button 
          className="text-gray-500 hover:text-gray-700"
          onClick={onClose}
        >
          <X size={20} />
        </button>
      </div>
      
      <div className="p-4">
        {inviteSent ? (
          <div className="py-8 text-center">
            <div className="mx-auto w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mb-4">
              <Check size={24} className="text-green-600" />
            </div>
            <h3 className="text-lg font-medium">Invitations Sent!</h3>
            <p className="text-gray-500 mt-1">
              {selectedUsers.length} {selectedUsers.length === 1 ? 'person has' : 'people have'} been invited to join your team
            </p>
          </div>
        ) : (
          <>
            <div className="mb-4 relative">
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <Search size={18} className="text-gray-400" />
              </div>
              <input
                type="text"
                className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary focus:border-primary block w-full pl-10 p-2.5"
                placeholder="Search by name"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            
            <div className="max-h-72 overflow-y-auto mb-4">
              {filteredResults.length > 0 ? (
                <ul className="space-y-2">
                  {filteredResults.map(user => (
                    <li 
                      key={user.id}
                      className={`flex items-center justify-between p-3 rounded-lg cursor-pointer ${
                        selectedUsers.includes(user.id) ? 'bg-primary bg-opacity-10' : 'bg-gray-50 hover:bg-gray-100'
                      }`}
                      onClick={() => toggleUserSelection(user.id)}
                    >
                      <div className="flex items-center">
                        <img src={user.avatar} alt={user.name} className="w-10 h-10 rounded-full mr-3" />
                        <div>
                          <p className="font-medium">{user.name}</p>
                          <p className="text-sm text-gray-500">Avg: {user.weeklyAvg}</p>
                        </div>
                      </div>
                      <div className={`w-5 h-5 rounded-full flex items-center justify-center ${
                        selectedUsers.includes(user.id) ? 'bg-primary text-white' : 'border border-gray-300'
                      }`}>
                        {selectedUsers.includes(user.id) && <Check size={12} />}
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  No users found matching "{searchQuery}"
                </div>
              )}
            </div>
            
            <div className="flex justify-between items-center">
              <div>
                <p className="text-sm text-gray-500">
                  {selectedUsers.length} {selectedUsers.length === 1 ? 'person' : 'people'} selected
                </p>
              </div>
              <button 
                className={`flex items-center px-4 py-2 rounded-md ${
                  selectedUsers.length > 0 ? 'bg-primary text-white' : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                }`}
                disabled={selectedUsers.length === 0}
                onClick={sendInvites}
              >
                <Send size={16} className="mr-2" />
                Send Invites
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default TeamInvite;